import React from 'react';

import { FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';

import type { LensPeriod, LensPeriodComparison, LensResolvedPeriod } from '~/types/leLens';

import {
  COMPARE_TO_PRESETS,
  DATE_RANGE_PRESETS,
  matchingCompareTo,
  matchingDateRangePreset,
  periodLabel,
} from '../lensPeriodPresets';

import type { PanelState } from './usePanelState';

const FOLLOWS_BOARD = 'board';
const NO_PERIOD = 'none';

type CardPeriodControlsProps = {
  boardPeriod: LensPeriod;
  resolved?: LensResolvedPeriod;
  state: PanelState;
};

/** A card's own date range and comparison: each follows the board unless pinned here. */
export default function CardPeriodControls({ boardPeriod, resolved, state }: CardPeriodControlsProps) {
  const pinned = state.draft.period;
  const own = pinned && typeof pinned === 'object' && !Array.isArray(pinned) ? pinned : undefined;
  const unbound = pinned === NO_PERIOD;
  const windowValue = unbound
    ? NO_PERIOD
    : own?.window
      ? (matchingDateRangePreset({ anchor: own.anchor, window: own.window })?.id ?? 'custom')
      : FOLLOWS_BOARD;
  const compareValue =
    own?.comparison !== undefined ? (matchingCompareTo(own.comparison)?.id ?? 'custom') : FOLLOWS_BOARD;
  const boardWindowLabel = periodLabel(boardPeriod);
  const boardCompareLabel = matchingCompareTo(boardPeriod.comparison)?.label ?? 'Custom dates';

  const pin = (patch: {
    window?: LensPeriod['window'] | null;
    comparison?: LensPeriodComparison | null | undefined;
  }) => {
    const next = { ...own };
    if (patch.window === null) {
      delete next.window;
    } else if (patch.window !== undefined) {
      next.window = patch.window;
    }
    if ('comparison' in patch) {
      if (patch.comparison === undefined) {
        delete next.comparison;
      } else {
        next.comparison = patch.comparison;
      }
    }
    if (patch.window !== undefined) {
      const preset =
        patch.window === null ? undefined : DATE_RANGE_PRESETS.find((candidate) => candidate.window === patch.window);
      if (preset?.anchor) {
        next.anchor = preset.anchor;
      } else {
        delete next.anchor;
      }
    }
    state.setPeriod(Object.keys(next).length ? next : undefined);
  };

  return (
    <Stack spacing={1.5}>
      <Typography color="text.secondary" sx={{ fontSize: 12 }}>
        Follows the Lens by default: {boardWindowLabel.toLowerCase()}
        {boardPeriod.comparison ? `, compared with ${boardCompareLabel.toLowerCase()}` : ''}.
      </Typography>
      <FormControl fullWidth size="small">
        <InputLabel id="lens-card-date-range">Date range</InputLabel>
        <Select
          label="Date range"
          labelId="lens-card-date-range"
          onChange={(event) => {
            const chosen = event.target.value;
            if (chosen === FOLLOWS_BOARD) {
              pin({ window: null });
            } else if (chosen === NO_PERIOD) {
              state.setPeriod(NO_PERIOD);
            } else {
              const preset = DATE_RANGE_PRESETS.find((candidate) => candidate.id === chosen);
              if (preset) {
                pin({ window: preset.window });
              }
            }
          }}
          value={windowValue}
        >
          <MenuItem value={FOLLOWS_BOARD}>Follows Lens ({boardWindowLabel})</MenuItem>
          {DATE_RANGE_PRESETS.map((preset) => (
            <MenuItem key={preset.id} value={preset.id}>
              {preset.label}
            </MenuItem>
          ))}
          {windowValue === 'custom' && <MenuItem value="custom">Custom dates</MenuItem>}
          <MenuItem value={NO_PERIOD}>No date range (reads every row)</MenuItem>
        </Select>
        <FormHelperText sx={{ ml: 0 }}>
          {unbound ? 'The period never reaches this card.' : (resolved?.label.current ?? 'Uses the Lens date range.')}
        </FormHelperText>
      </FormControl>
      {!unbound && (
        <FormControl fullWidth size="small">
          <InputLabel id="lens-card-compare-to">Compare to</InputLabel>
          <Select
            label="Compare to"
            labelId="lens-card-compare-to"
            onChange={(event) => {
              const chosen = event.target.value;
              if (chosen === FOLLOWS_BOARD) {
                pin({ comparison: undefined });
              } else {
                const preset = COMPARE_TO_PRESETS.find((candidate) => candidate.id === chosen);
                if (preset) {
                  pin({ comparison: preset.comparison });
                }
              }
            }}
            value={compareValue}
          >
            <MenuItem value={FOLLOWS_BOARD}>Follows Lens ({boardCompareLabel})</MenuItem>
            {COMPARE_TO_PRESETS.map((preset) => (
              <MenuItem key={preset.id} value={preset.id}>
                {preset.label}
              </MenuItem>
            ))}
            {compareValue === 'custom' && <MenuItem value="custom">Custom</MenuItem>}
          </Select>
          <FormHelperText sx={{ ml: 0 }}>
            {resolved?.label.comparison
              ? `${resolved.label.comparison}${!resolved.comparable ? ' · not comparable' : ''}`
              : 'No comparison on this card.'}
          </FormHelperText>
        </FormControl>
      )}
    </Stack>
  );
}
